import { Helmet } from 'react-helmet-async';
import * as React from 'react';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Grid,
  Tabs,
  Tab,
  Typography,
  Box,
  Accordion,
  CircularProgress,
  AccordionSummary,
  AccordionDetails,
  Skeleton
} from '@mui/material';
import { ReportingSummaryComponent, ReportingDetailComponent } from '../sections/reporting';
import { getGeneralAPIRequest } from '../utils/apiCommonMethods';
import { readPackageData } from '../utils/apiCommonReportingMethods';
import { allowedIESOSite } from '../apiConfig';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{
          p: 3, mx: 'auto', width: '100%',
          maxWidth: {
            xs: '95%',   // small screens → almost full width
            sm: 1000,    // tablets/laptops
            md: 1400,    // medium desktops
            lg: 1600,    // 24–27 inch monitors
            xl: 1800,    // ultra-wide monitors
          },
        }}>
          <Typography component="span">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function ReportingPage({ msSessionInfo, allSites }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [sitesWithPackages, setSitesWithPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleSiteTabChange = (newValue) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    const getAvailablePackages = async (site) => {
      if (!site || !msSessionInfo?.accessToken) return;

      const alreadyLoaded = sitesWithPackages.find((s) => s.site_id === site.id);
      if (alreadyLoaded) return;

      setIsLoading(true);

      try {
        const axiosSession = getGeneralAPIRequest(msSessionInfo);
        const paramsVar = new URLSearchParams();
        paramsVar.append('siteIds', site.id);
        const apiResponse = await axiosSession.get('/reporting/packages', { params: paramsVar });
        const newSiteData = readPackageData([{ ...site, site_id: site.id }], apiResponse.data);
        setSitesWithPackages((prev) => [...prev, ...newSiteData]);
      } catch (error) {
        if (error.response?.status === 401) {
          setSitesWithPackages((prev) => [
            ...prev,
            { ...site, site_id: site.id, availablePackages: [], detailReportingEnabled: false }
          ]);
        } else {
          console.error(`Error loading packages for site ${site.name}:`, error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (allSites && allSites[selectedTab]) {
      getAvailablePackages(allSites[selectedTab]);
    }
  }, [selectedTab, allSites, msSessionInfo, sitesWithPackages]);

  if (!allSites || allSites.length === 0) {
    return (
      <>
        <Helmet>
          <title>SYSO Reporting</title>
        </Helmet>

        <Box sx={{ pl: 5, pr: 5 }}>
          <Typography variant="h4" sx={{ pb: 1 }}>
            System Reporting
          </Typography>

          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1, pb: 1 }}>
              <Skeleton variant="rounded" width={120} height={36} />
              <Skeleton variant="rounded" width={140} height={36} />
              <Skeleton variant="rounded" width={130} height={36} />
              <Skeleton variant="rounded" width={110} height={36} />
            </Box>
          </Grid>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>SYSO Reporting</title>
      </Helmet>

      <Box sx={{ pl: 5, pr: 5 }}>
        <Typography variant="h4" sx={{ pb: 1 }}>
          System Reporting
        </Typography>
        <Grid item xs={12}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => handleSiteTabChange(newValue)}
              aria-label="SiteStatus"
              variant="scrollable"
              scrollButtons="auto"
            >
              {allSites?.map((site) => (
                <Tab key={site.id} label={site.name} />
              ))}
            </Tabs>
          </Box>

          {allSites?.map((site, index) => {
            const matchedSite = sitesWithPackages?.find((s) => s.site_id === site.id);
            const isIesoSite = allowedIESOSite.has(site.id);

            return (
              <TabPanel key={site.id} value={selectedTab} index={index}>
                {isLoading || !matchedSite ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '150px',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : matchedSite.availablePackages?.length > 0 ? (
                  <div>
                    {!isIesoSite && (
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content"
                          id="panel1-header">
                          Monthly Summary Reporting
                        </AccordionSummary>
                        <AccordionDetails>
                          <ReportingSummaryComponent
                            msSessionInfo={msSessionInfo}
                            singleSite={matchedSite}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {matchedSite.detailReportingEnabled && (
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content"
                          id="panel1-header">
                          {matchedSite.availablePackages?.some(pkg => pkg.apiName === 'iesoFinancialDetail')
                            ? 'Detail Reporting'
                            : 'Hourly Detail Reporting'}
                        </AccordionSummary>
                        <AccordionDetails>
                          <ReportingDetailComponent
                            msSessionInfo={msSessionInfo}
                            singleSite={matchedSite}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </div>
                ) : (
                  <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                    No reporting packages are available for {site.name}
                  </Typography>
                )}
              </TabPanel>
            );
          })}
        </Grid>
      </Box>
    </>
  );
}

ReportingPage.propTypes = {
  msSessionInfo: PropTypes.object.isRequired,
  allSites: PropTypes.array.isRequired,
};